<template>
  <span class="sticker-picker-wrap">
  <el-popover
    ref="popover"
    placement="top-start"
    trigger="click"
    v-model="visible"
    popper-class="sticker-popover"
    @show="onShow"
    @hide="closeAction"
  >
    <div class="sticker-container">
      <div v-if="currentPack === 'mine'" class="sticker-section">
        <div class="sticker-grid">
          <label class="sticker-item sticker-add">
            <input type="file" accept="image/*" hidden @change="onPickFile" />
            <span>+</span>
          </label>
          <div v-if="!mine.length" class="sticker-empty">点 + 从相册添加，或右键聊天/评论图片收藏</div>
          <div
            v-for="item in mine"
            :key="item.id || item.url"
            class="sticker-item"
            @click="select(item)"
            @mousedown="onPressStart('mine', item, $event)"
            @mouseup="onPressEnd"
            @mouseleave="onPressEnd"
            @touchstart="onPressStart('mine', item, $event)"
            @touchend="onPressEnd"
            @touchmove="onPressEnd"
            @contextmenu.prevent="onMineContext($event, item)"
          >
            <img :src="item.url" alt="" />
          </div>
        </div>
      </div>
      <div v-else class="sticker-section">
        <div class="sticker-grid">
          <div
            v-for="item in activeStickers"
            :key="item.id || item.url"
            class="sticker-item sticker-item--named"
            @click="select(item)"
            @mousedown="onPressStart('recommend', item, $event)"
            @mouseup="onPressEnd"
            @mouseleave="onPressEnd"
            @touchstart="onPressStart('recommend', item, $event)"
            @touchend="onPressEnd"
            @touchmove="onPressEnd"
            @contextmenu.prevent="onRecommendContext($event, item)"
          >
            <img :src="item.url" alt="" />
            <span v-if="item.name" class="sticker-name">{{ item.name }}</span>
          </div>
        </div>
      </div>
    </div>
    <span slot="reference">
      <slot>
        <span class="sticker-btn hand-style" title="斗图">
          <sticker-icon />
        </span>
      </slot>
    </span>
  </el-popover>
    <sticker-action-card
      :visible="actionVisible"
      :kind="actionKind"
      :url="actionItem && actionItem.url"
      :anchor="actionRect"
      @close="closeAction"
      @pin="onActionPin"
      @remove="onActionRemove"
      @add="onActionAdd"
    />
  </span>
</template>

<script>
import StickerActionCard from './ActionCard.vue'
import StickerIcon from '@/components/StickerIcon'

export default {
  name: 'StickerPicker',
  components: { StickerActionCard, StickerIcon },
  props: {
    mine: {
      type: Array,
      default: () => []
    },
    packs: {
      type: Array,
      default: () => []
    },
    activePack: {
      type: String,
      default: 'mine'
    }
  },
  computed: {
    currentPack() {
      return this.activePack || 'mine'
    },
    activeStickers() {
      const pack = (this.packs || []).find(item => item.id === this.currentPack)
      return pack && pack.stickers ? pack.stickers : []
    }
  },
  data() {
    return {
      visible: false,
      actionVisible: false,
      actionKind: 'mine',
      actionItem: null,
      actionRect: null,
      pressTimer: null,
      pressFired: false
    }
  },
  beforeDestroy() {
    this.clearPressTimer()
  },
  methods: {
    onShow() {
      this.closeAction()
      this.$emit('open')
    },
    /**
     * 供外部（工具栏上的表情包封面）程序化打开面板。
     *
     * 注意：调用方所在的那次 click 不能冒泡到 document。el-popover 在
     * trigger="click" 下会往 document 上挂 handleDocumentClick，点击点不在
     * reference / popper 里就把面板关掉；而 Vue 的 nextTick 是微任务，会在事件
     * 冒泡到 document 之前就把 visible=true 刷成 showPopper=true —— 于是「打开」
     * 和「关闭」落在同一次点击里，表现就是点了没反应。详见
     * mixins/stickerMixin.js 的 openStickerPackFromToolbar。
     */
    open() {
      this.visible = true
    },
    close() {
      this.visible = false
    },
    isOpen() {
      return this.visible
    },
    /**
     * 面板已打开时切换表情包：两个分组的网格行高不一样（「我添加的」72px、
     * 表情包 88px），内容高度变了得让 popper 重新算位置，否则 top-start
     * 这个方向上面板会和输入框叠在一起或者飘起来。
     */
    refresh() {
      this.$nextTick(() => {
        const popover = this.$refs.popover
        if (popover && popover.updatePopper) popover.updatePopper()
      })
    },
    select(item) {
      if (this.pressFired) {
        this.pressFired = false
        return
      }
      this.closeAction()
      this.$emit('select', item)
      this.visible = false
    },
    onPressStart(kind, item, e) {
      this.clearPressTimer()
      this.pressFired = false
      this.pressTimer = setTimeout(() => {
        this.pressFired = true
        this.openAction(kind, item, e)
      }, 500)
    },
    onPressEnd() {
      this.clearPressTimer()
    },
    clearPressTimer() {
      if (this.pressTimer) {
        clearTimeout(this.pressTimer)
        this.pressTimer = null
      }
    },
    onPickFile(e) {
      const file = e.target.files && e.target.files[0]
      e.target.value = ''
      if (file) this.$emit('upload', file)
    },
    rectFromEvent(e) {
      const el = e && e.currentTarget
      if (el && el.getBoundingClientRect) {
        const rect = el.getBoundingClientRect()
        return {
          left: rect.left,
          top: rect.top,
          width: rect.width,
          height: rect.height
        }
      }
      return {
        left: (e && e.clientX) || 0,
        top: (e && e.clientY) || 0,
        width: 0,
        height: 0
      }
    },
    openAction(kind, item, e) {
      if (!item) return
      this.actionKind = kind
      this.actionItem = item
      this.actionRect = this.rectFromEvent(e)
      this.actionVisible = true
    },
    closeAction() {
      this.actionVisible = false
      this.actionItem = null
      this.actionRect = null
    },
    onMineContext(e, item) {
      this.openAction('mine', item, e)
    },
    onRecommendContext(e, item) {
      this.openAction('recommend', item, e)
    },
    onActionPin() {
      const item = this.actionItem
      this.closeAction()
      if (item) this.$emit('pin', item)
    },
    onActionRemove() {
      const item = this.actionItem
      this.closeAction()
      if (item) this.$emit('remove', item)
    },
    onActionAdd() {
      const item = this.actionItem
      this.closeAction()
      if (!item) return
      if (item.added) {
        this.$message.success('已添加')
        return
      }
      this.$emit('add-recommend', item)
    }
  }
}
</script>

<style lang="scss">
.sticker-popover {
  padding: 12px 14px !important;
}
.sticker-container {
  width: 360px;
  max-height: 360px;
  overflow-y: auto;
}
.sticker-section {
  margin-bottom: 12px;
}
.sticker-section-title {
  font-size: 12px;
  color: #888;
  margin-bottom: 8px;
}
.sticker-grid {
  display: flex;
  flex-wrap: wrap;
}
.sticker-item {
  width: 72px;
  height: 72px;
  margin: 4px;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  background: #F5F5F5;
  display: flex;
  align-items: center;
  justify-content: center;
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}
.sticker-item--named {
  height: 88px;
  flex-direction: column;
  background: transparent;
  img {
    width: 64px;
    height: 64px;
  }
}
.sticker-name {
  font-size: 11px;
  color: #999;
  margin-top: 2px;
  max-width: 72px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sticker-add {
  border: 1px dashed #d0d0d0;
  background: #fff;
  color: #bbb;
  font-size: 28px;
  font-weight: 300;
}
.sticker-empty {
  flex: 1;
  min-width: 180px;
  font-size: 12px;
  color: #bbb;
  line-height: 1.5;
  display: flex;
  align-items: center;
  padding: 0 8px;
}
</style>
